import { useId, useRef, type KeyboardEvent, type ReactNode } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { cn } from '@eombor/ui';

export interface SceneTab<Value extends string> {
  value: Value;
  label: string;
  icon?: ReactNode;
}

export interface SceneTabsProps<Value extends string> {
  label: string;
  value: Value;
  tabs: readonly SceneTab<Value>[];
  onChange: (value: Value) => void;
  /** Tab paneli — `aria-controls` uchun. */
  panelId: string;
  /** Har tabning `id` si shu asosdan: panel `aria-labelledby` bilan unga bog'lanadi. */
  idOf: (value: Value) => string;
  className?: string;
}

/**
 * CHIZMA KO'RINISHI TABLARI (Fasad / 3D) — segmented control.
 *
 * Umumiy `Segmented` ataylab `role="group"` (u navigatsiya uchun). Bu yerda
 * esa bitta kartaning ichidagi PANEL almashadi, ya'ni semantik jihatdan bu
 * tab: `tablist`, `aria-selected`, ←/→ va Home/End bilan yurish, fokus
 * faqat faol tabda (roving tabindex).
 *
 * Faol tab ostidagi «pill» Framer Motion `layoutId` bilan siljiydi;
 * `prefers-reduced-motion` da sakrab o'tadi.
 */
export const SceneTabs = <Value extends string>({
  label,
  value,
  tabs,
  onChange,
  panelId,
  idOf,
  className,
}: SceneTabsProps<Value>) => {
  const reduce = useReducedMotion() === true;
  const pillId = useId();
  const refs = useRef(new Map<Value, HTMLButtonElement>());

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    const index = tabs.findIndex((tab) => tab.value === value);
    const last = tabs.length - 1;
    const next =
      event.key === 'ArrowRight'
        ? index === last ? 0 : index + 1
        : event.key === 'ArrowLeft'
          ? index === 0 ? last : index - 1
          : event.key === 'Home'
            ? 0
            : event.key === 'End'
              ? last
              : null;
    const tab = next === null ? undefined : tabs[next];
    if (tab === undefined) return;
    event.preventDefault();
    onChange(tab.value);
    refs.current.get(tab.value)?.focus();
  };

  return (
    <div
      role="tablist"
      aria-label={label}
      onKeyDown={onKeyDown}
      className={cn(
        'inline-flex gap-1 rounded-xl border border-border bg-surface-2/70 p-1',
        'shadow-[inset_0_1px_2px_rgb(10_30_50/0.04)]',
        className,
      )}
    >
      {tabs.map((tab) => {
        const active = tab.value === value;
        return (
          <button
            key={tab.value}
            ref={(node) => {
              if (node === null) refs.current.delete(tab.value);
              else refs.current.set(tab.value, node);
            }}
            id={idOf(tab.value)}
            type="button"
            role="tab"
            aria-selected={active}
            aria-controls={panelId}
            tabIndex={active ? 0 : -1}
            onClick={() => onChange(tab.value)}
            className={cn(
              'relative isolate inline-flex h-9 items-center gap-1.5 rounded-lg px-4',
              'text-sm font-medium whitespace-nowrap outline-none',
              'transition-[color,scale] duration-[var(--motion-fast)] ease-[var(--ease-standard)]',
              'focus-visible:ring-2 focus-visible:ring-accent active:scale-[0.97]',
              active ? 'text-text' : 'text-text-muted hover:text-text',
            )}
          >
            {active ? (
              <motion.span
                layoutId={pillId}
                aria-hidden
                className="absolute inset-0 -z-10 rounded-lg border border-border bg-surface shadow-[var(--shadow-2)]"
                transition={reduce ? { duration: 0 } : { type: 'spring', stiffness: 520, damping: 40 }}
              />
            ) : null}
            {tab.icon}
            {tab.label}
          </button>
        );
      })}
    </div>
  );
};
